// src/components/tokenPage/valuation/EfficiencyPanelCard.tsx

import React, { useState } from "react";
import { MetricBox } from "@/components/tokenPage/shared/TokenDashboardComponents";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { DataCard } from "@/components/ui";
import { useValuationMetrics } from "@/hooks/metrics";

type Range = '90d' | '180d' | '365d';

/**
 * EfficiencyPanelCard - Shows fee yield and ratio efficiency metrics
 * 
 * Uses useValuationMetrics hook for all calculations (no inline computation).
 */
const EfficiencyPanelCard: React.FC = () => {
  const [range, setRange] = useState<Range>('365d');
  const windowDays = range === '90d' ? 90 : range === '180d' ? 180 : 365;
  
  const {
    feeYieldPct,
    feesToTvlPct,
    mcFeesZScore,
    isLoading,
  } = useValuationMetrics(windowDays);

  // Hide if no data available
  const hasData = feeYieldPct != null || feesToTvlPct != null || mcFeesZScore != null;
  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="Efficiency"
      subtitle="Fee yield and ratio context"
      icon="gauge"
      iconColor="text-indigo-400"
      isLoading={isLoading}
      loadingVariant="stats"
      actions={<TimeRangePicker options={['90d','180d','365d'] as const} value={range} onChange={setRange} theme="blue" />}
    >
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <MetricBox label="Fees / Market Cap" value={feeYieldPct} fmt="pct1" toneBySign />
        <MetricBox label="Fees / TVL" value={feesToTvlPct} fmt="pct2" />
        <MetricBox label={`Market Cap / Fees z-score (${range})`} value={mcFeesZScore} fmt="ratio2" toneBySign />
      </div>
    </DataCard>
  );
};

export default EfficiencyPanelCard;
